import React, { useState, useEffect, useCallback } from 'react';
import api from '../services/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';
import {
  Building2, Plus, Trash2, Edit2, X, Loader2, Users, FileText,
  Eye, EyeOff, Power, PowerOff, Shield
} from 'lucide-react';

const AdminCompaniesPage = () => {
  const [companies, setCompanies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editing, setEditing] = useState(null);
  const [showPassword, setShowPassword] = useState(false);
  const [formData, setFormData] = useState({
    name: '', email: '', password: '', company_name: ''
  });

  const fetchCompanies = useCallback(async () => {
    try {
      const { data } = await api.get('/admin/companies');
      setCompanies(data);
    } catch (err) {
      if (err.response?.status === 403) {
        toast.error('Acceso denegado: solo administradores');
      } else {
        toast.error('Error cargando empresas');
      }
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchCompanies(); }, [fetchCompanies]);

  const resetForm = () => {
    setFormData({ name: '', email: '', password: '', company_name: '' });
    setEditing(null);
    setShowPassword(false);
  };

  const openModal = (company = null) => {
    if (company) {
      setEditing(company);
      setFormData({
        name: company.name || '',
        email: company.email || '',
        password: '',
        company_name: company.company_name || ''
      });
    } else {
      resetForm();
    }
    setShowModal(true);
  };

  const handleSubmit = async () => {
    if (!formData.company_name || !formData.email) {
      toast.error('Nombre de empresa y email son obligatorios');
      return;
    }
    if (!editing && !formData.password) {
      toast.error('La contraseña es obligatoria para nuevas empresas');
      return;
    }
    try {
      if (editing) {
        const payload = { ...formData };
        if (!payload.password) delete payload.password;
        await api.put(`/admin/companies/${editing.id}`, payload);
        toast.success('Empresa actualizada');
      } else {
        await api.post('/admin/companies', formData);
        toast.success('Empresa creada');
      }
      setShowModal(false);
      resetForm();
      fetchCompanies();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Error guardando empresa');
    }
  };

  const handleToggleActive = async (company) => {
    try {
      await api.put(`/admin/companies/${company.id}`, { active: !company.active });
      toast.success(company.active ? 'Empresa desactivada' : 'Empresa activada');
      fetchCompanies();
    } catch (err) {
      toast.error('Error actualizando estado');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('¿Eliminar esta empresa y TODOS sus datos? Esta acción es irreversible.')) return;
    try {
      await api.delete(`/admin/companies/${id}`);
      toast.success('Empresa eliminada');
      fetchCompanies();
    } catch (err) {
      toast.error('Error eliminando empresa');
    }
  };

  if (loading) {
    return <div className="flex items-center justify-center h-64"><Loader2 className="w-8 h-8 animate-spin text-[#FF6B4A]" /></div>;
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 sm:w-12 sm:h-12 rounded-xl bg-[#FF6B4A] flex items-center justify-center">
            <Shield className="w-5 h-5 sm:w-6 sm:h-6 text-white" />
          </div>
          <div>
            <h1 className="text-2xl sm:text-3xl font-heading font-extrabold tracking-tight" data-testid="admin-title">Gestión de Empresas</h1>
            <p className="text-stone-500 text-sm mt-0.5">Panel de administración</p>
          </div>
        </div>
        <Button onClick={() => openModal()} className="bg-[#FF6B4A] hover:bg-[#E05A3E] rounded-full shadow-lg shadow-orange-200/50 hover:-translate-y-0.5 transition-all duration-300 w-full sm:w-auto" data-testid="add-company-button">
          <Plus className="w-4 h-4 mr-2" /> Nueva Empresa
        </Button>
      </div>

      {/* Companies List */}
      {companies.length === 0 ? (
        <div className="bg-white rounded-3xl shadow-[0_8px_30px_rgb(0,0,0,0.04)] border border-stone-100 p-12 text-center" data-testid="no-companies">
          <Building2 className="w-12 h-12 text-stone-300 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-stone-600 mb-2">Sin empresas</h3>
          <p className="text-stone-400 text-sm mb-4">Crea empresas y asígnales un usuario y contraseña</p>
          <Button onClick={() => openModal()} className="bg-[#FF6B4A] hover:bg-[#E05A3E] rounded-full shadow-lg shadow-orange-200/50 hover:-translate-y-0.5 transition-all duration-300">
            <Plus className="w-4 h-4 mr-2" /> Crear Empresa
          </Button>
        </div>
      ) : (
        <div className="space-y-2">
          {companies.map((company) => (
            <div key={company.id} className={`bg-white border rounded-xl p-3 sm:p-4 flex flex-col sm:flex-row sm:items-center justify-between gap-3 transition-colors ${company.active !== false ? 'border-stone-100 hover:border-stone-300' : 'border-red-200 bg-red-50/30 opacity-70'}`} data-testid={`company-item-${company.id}`}>
              <div className="flex items-center gap-3 flex-1 min-w-0">
                <div className={`w-10 h-10 sm:w-12 sm:h-12 rounded-xl flex items-center justify-center flex-shrink-0 ${company.active !== false ? 'bg-orange-50' : 'bg-red-100'}`}>
                  <Building2 className={`w-5 h-5 ${company.active !== false ? 'text-[#FF6B4A]' : 'text-red-500'}`} />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <p className="font-medium text-sm truncate">{company.company_name}</p>
                    {company.active === false && (
                      <span className="text-[10px] px-1.5 py-0.5 bg-red-100 text-red-600 rounded font-medium">Desactivada</span>
                    )}
                  </div>
                  <p className="text-xs text-stone-500 truncate">{company.name} · {company.email}</p>
                  <div className="flex items-center gap-3 mt-1">
                    <span className="text-[10px] text-stone-400 flex items-center gap-1">
                      <FileText className="w-3 h-3" /> {company.invoices_count} facturas
                    </span>
                    <span className="text-[10px] text-stone-400 flex items-center gap-1">
                      <Users className="w-3 h-3" /> {company.clients_count} clientes
                    </span>
                  </div>
                </div>
              </div>
              <div className="flex gap-1 sm:gap-2">
                <Button variant="ghost" size="sm" onClick={() => handleToggleActive(company)} className="rounded-xl" title={company.active !== false ? 'Desactivar' : 'Activar'} data-testid={`toggle-company-${company.id}`}>
                  {company.active !== false ? <Power className="w-3.5 h-3.5 text-green-600" /> : <PowerOff className="w-3.5 h-3.5 text-red-500" />}
                </Button>
                <Button variant="ghost" size="sm" onClick={() => openModal(company)} className="rounded-xl" data-testid={`edit-company-${company.id}`}>
                  <Edit2 className="w-3.5 h-3.5" />
                </Button>
                <Button variant="ghost" size="sm" onClick={() => handleDelete(company.id)} className="rounded-xl text-red-500 hover:text-red-700" data-testid={`delete-company-${company.id}`}>
                  <Trash2 className="w-3.5 h-3.5" />
                </Button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-xl w-full max-w-md">
            <div className="flex items-center justify-between p-4 sm:p-6 border-b border-stone-100">
              <h2 className="text-lg font-heading font-bold">{editing ? 'Editar Empresa' : 'Nueva Empresa'}</h2>
              <button onClick={() => { setShowModal(false); resetForm(); }} className="p-1 hover:bg-stone-100 rounded-xl"><X className="w-5 h-5" /></button>
            </div>
            <div className="p-4 sm:p-6 space-y-4">
              <div>
                <Label>Nombre de la Empresa *</Label>
                <Input value={formData.company_name} onChange={(e) => setFormData(p => ({ ...p, company_name: e.target.value }))} placeholder="Empresa SL" className="rounded-xl" data-testid="company-name-input" />
              </div>
              <div>
                <Label>Nombre del Responsable</Label>
                <Input value={formData.name} onChange={(e) => setFormData(p => ({ ...p, name: e.target.value }))} placeholder="Juan García" className="rounded-xl" data-testid="company-person-input" />
              </div>
              <div>
                <Label>Email (para iniciar sesión) *</Label>
                <Input type="email" value={formData.email} onChange={(e) => setFormData(p => ({ ...p, email: e.target.value }))} placeholder="empresa@email.com" className="rounded-xl" data-testid="company-email-input" />
              </div>
              <div>
                <Label>{editing ? 'Nueva Contraseña (dejar vacío para no cambiar)' : 'Contraseña *'}</Label>
                <div className="relative">
                  <Input type={showPassword ? 'text' : 'password'} value={formData.password} onChange={(e) => setFormData(p => ({ ...p, password: e.target.value }))} placeholder={editing ? 'Sin cambios' : 'Contraseña segura'} className="rounded-xl pr-10" data-testid="company-password-input" />
                  <button type="button" onClick={() => setShowPassword(!showPassword)} className="absolute right-2 top-1/2 -translate-y-1/2 p-1 text-stone-400 hover:text-stone-600">
                    {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>
              </div>
            </div>
            <div className="flex gap-3 justify-end p-4 sm:p-6 border-t border-stone-100">
              <Button variant="outline" onClick={() => { setShowModal(false); resetForm(); }} className="rounded-xl">Cancelar</Button>
              <Button onClick={handleSubmit} className="bg-[#FF6B4A] hover:bg-[#E05A3E] rounded-full shadow-lg shadow-orange-200/50 hover:-translate-y-0.5 transition-all duration-300" data-testid="save-company-button">
                {editing ? 'Actualizar' : 'Crear Empresa'}
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminCompaniesPage;
